<template>
  <div>
    <div id="deliverySection" class="columns-container container-spacing">
      <div class="column">
        <div class="change-order-page__header-left section-header">
          <h3>
            {{ i18n.t('/change-order/sections/delivery-dates') }}
          </h3>
        </div>
        <div class="expectedDeliveryDate">
          <label>
            {{ i18n.t('/change-order/delivery-dates/expected-delivery-date') }}
            <br />
            {{ i18n.t('/change-order/delivery-dates/arrive-dealer') }}
          </label>
          <span>
            {{ getOriginalPriceReferenceDate }}
          </span>
        </div>

        <div style="margin-top: 30px">
          <div v-if="getOriginalNotDeliverBeforeDate" class="expectedDeliveryDate">
            <label>
              {{ i18n.t('/expected delivery date/donotdeliverbefore') }}
            </label>
            <span>
              {{ getOriginalNotDeliverBeforeDate }}
            </span>
          </div>

          <div v-if="getOriginalDeliverAsSoonAsPossible" class="expectedDeliveryDate">
            <label>
              {{ i18n.t('/expected delivery date/deliverassoonaspossible') }}
            </label>
          </div>
        </div>
      </div>

      <div class="column">
        <div class="change-order-page__header-right section-header">
          <h3>
            {{ i18n.t('/change-order/sections/delivery-dates') }}
          </h3>
        </div>

        <div class="expectedDeliveryDate" :class="determineChangedPriceReferenceDateClass">
          <label>
            {{ i18n.t('/change-order/delivery-dates/expected-delivery-date') }}
            <br />
            {{ i18n.t('/change-order/delivery-dates/arrive-dealer') }}
          </label>
          <span>
            {{ getChangedPriceReferenceDate }}
          </span>
        </div>
        <!--  ======================================================== -->
        <div v-if="showInput">
          <div class="delivery-options-container">
            <form-radio-button-list
              id="deliver-as-soon-as-possible"
              data-qa="deliver-as-soon-as-possible"
              title=""
              :options="doNotDeliverBeforeOptions"
              :model-value="doNotDeliverBeforeDateRadioValue"
              @change="toggleDoNotDeliverBefore"
            >
            </form-radio-button-list>

            <div class="datepicker-container">
              <date-picker
                v-show="doNotDeliverBeforeDateChecked"
                id="doNotDeliverBeforeDate"
                :date="doNotDeliverBeforeDateComputed"
                :title="''"
                :min-date="defaultMinDate"
                :max-date="maxDate"
                :error-message="errorMessageDoNotDeliverBeforeDate"
                @change-date="changeDoNotDeliverBeforeDate"
              />
            </div>
          </div>

          <div class="delivery-date-data-input-container">
            <app-button data-qa="confirm-K-code" @click="confirmDeliveryDate">
              {{ i18n.t('/common/confirm') }}
            </app-button>
            <app-button data-qa="cancel-Add-Mode" @click="cancelEditMode">
              {{ i18n.t('/common/cancel') }}
            </app-button>
          </div>
        </div>
        <div v-else>
          <span v-if="hasChangedDeliveryDate" class="confirmation-message">
            <i class="fas fa-arrows-rotate"></i>&nbsp;
            <strong>
              {{ i18n.t('/change-order/generic/modified') }}
            </strong>
            &nbsp; {{ i18n.t('/change-order/generic/modified-confirmation') }}
          </span>

          <div class="delivery-date-panel">
            <div class="expectedDeliveryDate">
              <label class="label" :class="determineDeliveryDateChangedClass">
                {{ formattedDoNotDeliverBeforeOption }}
              </label>
              <span
                v-if="formattedChangedNotDeliverBeforeDate"
                :class="determineDeliveryDateChangedClass"
              >
                {{ formattedChangedNotDeliverBeforeDate }}
              </span>
            </div>
            <div class="delivery-date-button-panel">
              <app-button
                data-qa="enter-edit-mode"
                :title="retrieveLockedReasons"
                @click="enterEditMode"
              >
                <i :class="determineChangedClass"></i>&nbsp;
                {{ i18n.t('/edit button label') }}
              </app-button>
            </div>
          </div>
        </div>
        <!-- ======================================================== -->
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import FormRadioButtonList from '@/components/generic/presentational/atoms/formRadioButtonList/FormRadioButtonList.vue'
import DatePicker from '@/components/generic/presentational/atoms/datePicker/DatePicker.vue'
import moment from 'moment/moment'
import { useVuelidate } from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import ChangeOrderStatus from '@/helpers/enums/changeOrderStatus'

export default {
  components: {
    AppButton,
    DatePicker,
    FormRadioButtonList,
  },
  validations() {
    const NON_DELIVERY = 'NON_DELIVERY'

    const isAfterMinDate = (value, options) => {
      const { type = '' } = options

      const minDate = type === NON_DELIVERY ? this.defaultMinDate : this.minDate

      return !helpers.req(value) || (value !== 'INVALID' && value.isSameOrAfter(minDate, 'day'))
    }

    const isBeforeMaxDate = (value) => {
      const maxDate = this.maxDate

      return !helpers.req(value) || (value !== 'INVALID' && value.isSameOrBefore(maxDate, 'day'))
    }

    const isValidDate = (value) => {
      return !helpers.req(value) || moment.utc(value).isValid()
    }

    return {
      deliveryDate: {
        required,
        isAfterMinDate: (value) => isAfterMinDate(value, { type: '' }),
        isValidDate,
      },
      doNotDeliverBeforeDate: {
        isValidDate,
        isAfterMinDate: (value) => isAfterMinDate(value, { type: NON_DELIVERY }),
        isBeforeMaxDate,
      },
    }
  },
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      doNotDeliverBeforeDate: null,
      doNotDeliverBeforeDateRadioValue: null,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      remark: '',
      disable: false,
      showInput: false,
    }
  },
  computed: {
    ...mapGetters({
      changeOrder: 'changeOrder/get',
      dealerSettings: 'user/dealerSettings',
      doNotDeliverBeforeEditEnabled: 'user/doNotDeliverBeforeEditEnabled',
    }),
    doNotDeliverBeforeOptions() {
      return [
        {
          name: this.i18n.t('/expected delivery date/deliverassoonaspossible'),
          value: 'deliverAsSoonAsPossible',
        },
        {
          name: this.i18n.t('/expected delivery date/donotdeliverbefore'),
          value: 'doNotDeliverBefore',
        },
      ]
    },
    doNotDeliverBeforeDateChecked() {
      return this.doNotDeliverBeforeDateRadioValue === 'doNotDeliverBefore'
    },
    doNotDeliverBeforeDateComputed() {
      return this.doNotDeliverBeforeDate || this.deliveryDate
    },
    defaultMinDate() {
      return moment.utc().startOf('day')
    },
    minDate() {
      return this.defaultMinDate
    },
    maxDate() {
      return moment.utc().add(2, 'years').startOf('day')
    },
    errorMessageDoNotDeliverBeforeDate() {
      if (this.doNotDeliverBeforeDateChecked && this.v$.doNotDeliverBeforeDate.$anyDirty) {
        if (!this.v$.doNotDeliverBeforeDate.isValidDate.$response)
          return 'Vul een geldige niet leverdatum voor in (dd-mm-jjjj)'
        if (!this.v$.doNotDeliverBeforeDate.isAfterMinDate.$response)
          return 'De niet leverdatum voor moet vanaf vandaag plaatsvinden'
        if (!this.v$.doNotDeliverBeforeDate.isBeforeMaxDate.$response)
          return 'De niet leveren voor datum moet niet meer dan 2 jaar in de toekomst plaatsvinden'
      }
      return ''
    },
    deliveryDate() {
      return null
    },
    formattedDoNotDeliverBeforeOption() {
      if (this.doNotDeliverBeforeDateRadioValue) {
        return this.doNotDeliverBeforeOptions.find(
          (o) => o.value === this.doNotDeliverBeforeDateRadioValue
        ).name
      }
      return null
    },
    formattedChangedNotDeliverBeforeDate() {
      return this.changeOrder.DeliveryDate.ChangedNotDeliverBeforeDate
    },
    hasChangedChangedPriceReferenceDate() {
      let originalPriceReferenceDate = this.changeOrder.CarConfiguration.OriginalPriceReferenceDate
      let changedPriceReferenceDate = this.changeOrder.CarConfiguration.ChangedPriceReferenceDate

      return originalPriceReferenceDate !== changedPriceReferenceDate
    },
    determineChangedPriceReferenceDateClass() {
      if (this.hasChangedChangedPriceReferenceDate) {
        return 'price-reference-date-changed'
      }
      return ''
    },
    hasChangedDeliveryDate() {
      let originalNotDeliverBeforeDate = this.changeOrder.DeliveryDate.OriginalNotDeliverBeforeDate
      let changedNotDeliverBeforeDate = this.changeOrder.DeliveryDate.ChangedNotDeliverBeforeDate

      let originalDeliverAsSoonAsPossible =
        this.changeOrder.DeliveryDate.OriginalDeliverAsSoonAsPossible
      let changedDeliverAsSoonAsPossible =
        this.changeOrder.DeliveryDate.ChangedDeliverAsSoonAsPossible

      return (
        originalNotDeliverBeforeDate !== changedNotDeliverBeforeDate ||
        originalDeliverAsSoonAsPossible !== changedDeliverAsSoonAsPossible
      )
    },
    determineDeliveryDateChangedClass() {
      if (this.hasChangedDeliveryDate) {
        return 'delivery-date-changed'
      }
      return ''
    },
    determineChangedClass() {
      if (this.canEditDeliveryDate) {
        return 'fa-solid fa-pencil'
      } else {
        return 'fa-solid fa-lock'
      }
    },
    canDeliveryDateChange() {
      return (
        this.changeOrder.Permissions.CanChangeDeliveryDates.Allowed === true &&
        this.changeOrder.DeliveryDate.OrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.archived
      )
    },
    canEditDeliveryDate() {
      return this.canDeliveryDateChange && this.doNotDeliverBeforeEditEnabled
    },
    retrieveLockedReasons() {
      let lockedReasons = []
      if (!this.canDeliveryDateChange) {
        if (!this.changeOrder.Permissions?.CanChangeDeliveryDates?.Allowed) {
          lockedReasons.push(this.changeOrder.Permissions?.CanChangeDeliveryDates?.Reason)
        }
        if (
          this.changeOrder.CanChangeDeliveryDates?.OrderStatus === ChangeOrderStatus.submitted ||
          this.changeOrder.MainOrderStatus === ChangeOrderStatus.submitted ||
          this.changeOrder.MainOrderStatus === ChangeOrderStatus.archived
        ) {
          lockedReasons.push('Order has been submitted or deleted')
        }
      }
      if (!this.doNotDeliverBeforeEditEnabled) {
        lockedReasons.push(this.i18n.t('/change-order/delivery-dates/dealer-restriction-locked'))
      }
      return lockedReasons.join('\n')
    },
    getOriginalPriceReferenceDate() {
      return this.changeOrder.CarConfiguration.OriginalPriceReferenceDate
    },
    getChangedPriceReferenceDate() {
      return this.changeOrder.CarConfiguration.ChangedPriceReferenceDate
    },
    getOriginalDeliverAsSoonAsPossible() {
      return this.changeOrder.DeliveryDate.OriginalDeliverAsSoonAsPossible
    },
    getOriginalNotDeliverBeforeDate() {
      return this.changeOrder.DeliveryDate.OriginalNotDeliverBeforeDate
    },
  },
  created() {
    this.$nextTick(() => {
      this.v$.deliveryDate.$touch()
    })
  },
  mounted() {
    this.setDeliveryDate()
  },
  methods: {
    convertToMilliseconds(dateString) {
      const parts = dateString.split('-')
      const date = new Date(parts[2], parts[1] - 1, parts[0])
      return date.getTime()
    },
    convertFromMilliseconds(milliseconds) {
      const date = new Date(milliseconds)
      const day = ('0' + date.getDate()).slice(-2)
      const month = ('0' + (date.getMonth() + 1)).slice(-2)
      const year = date.getFullYear()
      return `${day}-${month}-${year}`
    },
    setDeliveryDate() {
      if (this.changeOrder.DeliveryDate.ChangedNotDeliverBeforeDate) {
        this.doNotDeliverBeforeDateRadioValue = 'doNotDeliverBefore'

        this.doNotDeliverBeforeDate = this.convertToMilliseconds(
          this.changeOrder.DeliveryDate.ChangedNotDeliverBeforeDate
        )
      }

      if (this.changeOrder.DeliveryDate.ChangedDeliverAsSoonAsPossible) {
        this.doNotDeliverBeforeDateRadioValue = 'deliverAsSoonAsPossible'

        let originalNotDeliverBeforeDate =
          this.changeOrder.DeliveryDate.OriginalNotDeliverBeforeDate
        if (originalNotDeliverBeforeDate) {
          this.doNotDeliverBeforeDate = this.convertToMilliseconds(originalNotDeliverBeforeDate)
        }
      }
    },
    cancelEditMode() {
      this.showInput = false
    },
    confirmDeliveryDate() {
      if (this.doNotDeliverBeforeDateRadioValue === 'doNotDeliverBefore') {
        this.changeOrder.DeliveryDate.ChangedNotDeliverBeforeDate = this.convertFromMilliseconds(
          this.doNotDeliverBeforeDate
        )
        this.changeOrder.DeliveryDate.ChangedDeliverAsSoonAsPossible = false
      }

      if (this.doNotDeliverBeforeDateRadioValue === 'deliverAsSoonAsPossible') {
        this.changeOrder.DeliveryDate.ChangedDeliverAsSoonAsPossible = true
        this.changeOrder.DeliveryDate.ChangedNotDeliverBeforeDate = null
      }

      this.$store.commit('changeOrder/setDeliveryDateStatus')
      this.showInput = false
    },
    enterEditMode() {
      if (this.canEditDeliveryDate) {
        this.showInput = true
      }
    },
    toggleDoNotDeliverBefore(event) {
      this.doNotDeliverBeforeDateRadioValue = event
    },
    changeDoNotDeliverBeforeDate(value) {
      this.doNotDeliverBeforeDate = value
      this.v$.doNotDeliverBeforeDate.$touch()
    },
  },
}
</script>
<style lang="scss" scoped>
.columns-container {
  display: flex;
  justify-content: space-between;
}

.container-spacing {
  margin-top: 50px;
}

.column {
  flex: 1;
  margin: 0 20px;
}

.expectedDeliveryDate {
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 30px;
}

.expectedDeliveryDate label {
  font-weight: 700;
}

.expectedDeliveryDate span {
  margin-left: auto;
  margin-right: 50px;
}

h3 {
  font-weight: 400;
  margin: 0;
}

.change-order {
  &-page__header-left {
    h3 {
      color: $black;
    }
  }

  &-page__header-right {
    h3 {
      color: $color-primary;
    }
  }
}

.section-header {
  border-bottom: 1px solid #a3b8c0;
  align-items: center;
  margin-bottom: 1rem;
}

.delivery-date-data-input-container {
  margin-top: 40px;
  margin-bottom: 40px;
}

.confirmation-message {
  background-color: #ffdd9c;
  font-size: 18px;
  padding: 10px;
}

.delivery-options-container {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  .datepicker-container {
    min-height: 52px;
  }
}

.delivery-date-panel {
  margin-top: 20px;
  label {
    font-weight: bold;
    margin-bottom: 3px;
  }
  input {
    width: 400px;
    border: 1px solid black;
  }
}

.price-reference-date-changed {
  background-color: #fff8b9;
}

.delivery-date-changed {
  background-color: #fff8b9;
}

.delivery-date-button-panel {
  margin-top: 20px;
}
</style>
